export type ImageUploaderSourceType = 'upload' | 'link' | 'unsplash' | 'color';

export type ImageUploaderAssetAttribution = {
  author: string;
  authorUrl: string;
  sourceUrl: string;
};

export type ImageUploaderCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageUploaderEditState = {
  crop: ImageUploaderCropRect;
  targetWidth: number;
  targetHeight: number;
};

export type ImageUploaderCacheVariantRecord = {
  storageKey: string;
  cacheKey: string;
  assetId: string;
  assetStorageKey: string;
  scale: 0 | 1 | 2;
  descriptor: 'editable' | '1x' | '2x';
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
};

export type ImageUploaderCacheVariantManifest = Omit<
  ImageUploaderCacheVariantRecord,
  'cacheKey' | 'assetId' | 'assetStorageKey' | 'blob'
>;

export type ImageUploaderCachePreviewRecord = {
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
};

export type ImageUploaderPersistedVariantRef = {
  scale: 0 | 1 | 2;
  descriptor: 'editable' | '1x' | '2x';
  url?: string;
  filename?: string;
  imageUploadId?: string;
};

export type ImageUploaderPersistedAssetRef = {
  url?: string;
  filename?: string;
  imageUploadId?: string;
  variants: ImageUploaderPersistedVariantRef[];
};

export type ImageUploaderCacheManifestRecord = {
  storageKey: string;
  cacheKey: string;
  id: string;
  source: ImageUploaderSourceType;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  createdAt: string;
  lastAccessedAt: string;
  totalBytes: number;
  attribution?: ImageUploaderAssetAttribution;
  edit?: ImageUploaderEditState;
  persisted?: ImageUploaderPersistedAssetRef;
  preview: ImageUploaderCachePreviewRecord;
  variants: ImageUploaderCacheVariantManifest[];
};

export type ImageUploaderCacheRecord = Omit<ImageUploaderCacheManifestRecord, 'variants'> & {
  variants: ImageUploaderCacheVariantRecord[];
};

export type ImageUploaderCacheRecordInput = {
  cacheKey: string;
  id: string;
  source: ImageUploaderSourceType;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  createdAt: string;
  lastAccessedAt: string;
  totalBytes: number;
  attribution?: ImageUploaderAssetAttribution;
  edit: ImageUploaderEditState;
  persisted?: ImageUploaderPersistedAssetRef;
  preview: ImageUploaderCachePreviewRecord;
  variants: Array<{
    scale: 0 | 1 | 2;
    descriptor: 'editable' | '1x' | '2x';
    blob: Blob;
    width: number;
    height: number;
    bytes: number;
    mimeType: string;
  }>;
};

type SaveCacheOptions = {
  maxItems: number;
  maxTotalBytes: number;
};

const DB_NAME = 'flowmap-image-uploader-cache';
const DB_VERSION = 1;
const MANIFEST_STORE = 'image-uploader-manifests';
const VARIANT_STORE = 'image-uploader-variants';
const MANIFEST_CACHE_KEY_INDEX = 'cacheKey';
const VARIANT_ASSET_STORAGE_KEY_INDEX = 'assetStorageKey';

let databasePromise: Promise<IDBDatabase> | null = null;

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function waitForTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
}

function getDatabase(): Promise<IDBDatabase> {
  if (databasePromise) {
    return databasePromise;
  }

  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable in this browser'));
  }

  const nextDatabasePromise: Promise<IDBDatabase> = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;

      if (!database.objectStoreNames.contains(MANIFEST_STORE)) {
        const manifestStore = database.createObjectStore(MANIFEST_STORE, {
          keyPath: 'storageKey',
        });
        manifestStore.createIndex(MANIFEST_CACHE_KEY_INDEX, 'cacheKey', { unique: false });
      }

      if (!database.objectStoreNames.contains(VARIANT_STORE)) {
        const variantStore = database.createObjectStore(VARIANT_STORE, {
          keyPath: 'storageKey',
        });
        variantStore.createIndex(VARIANT_ASSET_STORAGE_KEY_INDEX, 'assetStorageKey', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB'));
  }).catch(error => {
    databasePromise = null;
    throw error;
  });

  databasePromise = nextDatabasePromise;
  return nextDatabasePromise;
}

function getManifestStorageKey(cacheKey: string, id: string): string {
  return `${cacheKey}:${id}`;
}

function getVariantStorageKey(
  assetStorageKey: string,
  descriptor: ImageUploaderCacheVariantRecord['descriptor'],
): string {
  return `${assetStorageKey}:${descriptor}`;
}

function sortManifests(records: ImageUploaderCacheManifestRecord[]): ImageUploaderCacheManifestRecord[] {
  return [...records].sort((left, right) => {
    return Date.parse(right.lastAccessedAt || right.createdAt) - Date.parse(left.lastAccessedAt || left.createdAt);
  });
}

function applyManifestLimits(
  manifests: ImageUploaderCacheManifestRecord[],
  options: SaveCacheOptions,
): ImageUploaderCacheManifestRecord[] {
  const sorted = sortManifests(manifests);
  const safeMaxItems = Math.max(1, options.maxItems);
  const safeMaxTotalBytes = Math.max(1, options.maxTotalBytes);

  let totalBytes = 0;
  const kept: ImageUploaderCacheManifestRecord[] = [];

  for (const manifest of sorted) {
    if (kept.length >= safeMaxItems) {
      continue;
    }

    if (kept.length > 0 && totalBytes + manifest.totalBytes > safeMaxTotalBytes) {
      continue;
    }

    kept.push(manifest);
    totalBytes += manifest.totalBytes;
  }

  if (kept.length === 0 && sorted[0]) {
    kept.push(sorted[0]);
  }

  return kept;
}

async function listManifests(cacheKey: string): Promise<ImageUploaderCacheManifestRecord[]> {
  const database = await getDatabase();
  const transaction = database.transaction(MANIFEST_STORE, 'readonly');
  const store = transaction.objectStore(MANIFEST_STORE);
  const index = store.index(MANIFEST_CACHE_KEY_INDEX);
  const manifests = (await requestToPromise(
    index.getAll(IDBKeyRange.only(cacheKey)),
  )) as ImageUploaderCacheManifestRecord[];

  await waitForTransaction(transaction);
  return sortManifests(manifests);
}

async function putRecord(input: ImageUploaderCacheRecordInput): Promise<ImageUploaderCacheManifestRecord> {
  const database = await getDatabase();
  const assetStorageKey = getManifestStorageKey(input.cacheKey, input.id);
  const transaction = database.transaction([MANIFEST_STORE, VARIANT_STORE], 'readwrite');
  const manifestStore = transaction.objectStore(MANIFEST_STORE);
  const variantStore = transaction.objectStore(VARIANT_STORE);

  const manifest: ImageUploaderCacheManifestRecord = {
    storageKey: assetStorageKey,
    cacheKey: input.cacheKey,
    id: input.id,
    source: input.source,
    width: input.width,
    height: input.height,
    bytes: input.bytes,
    mimeType: input.mimeType,
    createdAt: input.createdAt,
    lastAccessedAt: input.lastAccessedAt,
    totalBytes: input.totalBytes,
    ...(input.attribution ? { attribution: input.attribution } : {}),
    edit: input.edit,
    ...(input.persisted ? { persisted: input.persisted } : {}),
    preview: input.preview,
    variants: input.variants.map(variant => ({
      storageKey: getVariantStorageKey(assetStorageKey, variant.descriptor),
      scale: variant.scale,
      descriptor: variant.descriptor,
      width: variant.width,
      height: variant.height,
      bytes: variant.bytes,
      mimeType: variant.mimeType,
    })),
  };

  manifestStore.put(manifest);

  for (const variant of input.variants) {
    const record: ImageUploaderCacheVariantRecord = {
      storageKey: getVariantStorageKey(assetStorageKey, variant.descriptor),
      cacheKey: input.cacheKey,
      assetId: input.id,
      assetStorageKey,
      scale: variant.scale,
      descriptor: variant.descriptor,
      blob: variant.blob,
      width: variant.width,
      height: variant.height,
      bytes: variant.bytes,
      mimeType: variant.mimeType,
    };

    variantStore.put(record);
  }

  await waitForTransaction(transaction);
  return manifest;
}

async function deleteRecordByStorageKey(assetStorageKey: string): Promise<void> {
  const database = await getDatabase();
  const transaction = database.transaction([MANIFEST_STORE, VARIANT_STORE], 'readwrite');
  const manifestStore = transaction.objectStore(MANIFEST_STORE);
  const variantStore = transaction.objectStore(VARIANT_STORE);
  const variantIndex = variantStore.index(VARIANT_ASSET_STORAGE_KEY_INDEX);

  manifestStore.delete(assetStorageKey);

  const variantKeys = (await requestToPromise(
    variantIndex.getAllKeys(IDBKeyRange.only(assetStorageKey)),
  )) as IDBValidKey[];

  for (const key of variantKeys) {
    variantStore.delete(key);
  }

  await waitForTransaction(transaction);
}

export async function listImageUploaderCacheManifests(cacheKey: string): Promise<ImageUploaderCacheManifestRecord[]> {
  return listManifests(cacheKey);
}

export async function getImageUploaderCacheRecord(
  cacheKey: string,
  id: string,
): Promise<ImageUploaderCacheRecord | null> {
  const database = await getDatabase();
  const assetStorageKey = getManifestStorageKey(cacheKey, id);
  const transaction = database.transaction([MANIFEST_STORE, VARIANT_STORE], 'readonly');
  const manifestStore = transaction.objectStore(MANIFEST_STORE);
  const variantStore = transaction.objectStore(VARIANT_STORE);
  const variantIndex = variantStore.index(VARIANT_ASSET_STORAGE_KEY_INDEX);

  const manifest = (await requestToPromise(manifestStore.get(assetStorageKey))) as
    | ImageUploaderCacheManifestRecord
    | undefined;

  if (!manifest) {
    await waitForTransaction(transaction);
    return null;
  }

  const variants = (await requestToPromise(
    variantIndex.getAll(IDBKeyRange.only(assetStorageKey)),
  )) as ImageUploaderCacheVariantRecord[];

  await waitForTransaction(transaction);

  return {
    ...manifest,
    variants: variants.sort((left, right) => left.scale - right.scale),
  };
}

export async function saveImageUploaderCacheRecord(
  input: ImageUploaderCacheRecordInput,
  options: SaveCacheOptions,
): Promise<ImageUploaderCacheManifestRecord[]> {
  const existing = await listManifests(input.cacheKey);
  const nextManifest = await putRecord(input);

  const merged = [nextManifest, ...existing.filter(item => item.id !== input.id)];
  const kept = applyManifestLimits(merged, options);
  const keptKeys = new Set(kept.map(item => item.storageKey));
  const removed = merged.filter(item => !keptKeys.has(item.storageKey));

  for (const item of removed) {
    await deleteRecordByStorageKey(item.storageKey);
  }

  return listManifests(input.cacheKey);
}

export async function clearImageUploaderCache(cacheKey: string): Promise<void> {
  const manifests = await listManifests(cacheKey);

  for (const manifest of manifests) {
    await deleteRecordByStorageKey(manifest.storageKey);
  }
}

export async function touchImageUploaderCacheRecord(
  cacheKey: string,
  id: string,
): Promise<ImageUploaderCacheManifestRecord[]> {
  const record = await getImageUploaderCacheRecord(cacheKey, id);
  if (!record) {
    return listManifests(cacheKey);
  }

  const input: ImageUploaderCacheRecordInput = {
    cacheKey,
    id: record.id,
    source: record.source,
    width: record.width,
    height: record.height,
    bytes: record.bytes,
    mimeType: record.mimeType,
    createdAt: record.createdAt,
    lastAccessedAt: new Date().toISOString(),
    totalBytes: record.totalBytes,
    ...(record.attribution ? { attribution: record.attribution } : {}),
    edit: record.edit ?? {
      crop: { x: 0, y: 0, width: 100, height: 100 },
      targetWidth: record.width,
      targetHeight: record.height,
    },
    ...(record.persisted ? { persisted: record.persisted } : {}),
    preview: record.preview,
    variants: record.variants.map(variant => ({
      scale: variant.scale,
      descriptor: variant.descriptor,
      blob: variant.blob,
      width: variant.width,
      height: variant.height,
      bytes: variant.bytes,
      mimeType: variant.mimeType,
    })),
  };

  await putRecord(input);
  return listManifests(cacheKey);
}

export async function updateImageUploaderCachePersistedAsset(
  cacheKey: string,
  id: string,
  persisted: ImageUploaderPersistedAssetRef,
): Promise<ImageUploaderCacheManifestRecord[]> {
  const record = await getImageUploaderCacheRecord(cacheKey, id);
  if (!record) {
    return listManifests(cacheKey);
  }

  const input: ImageUploaderCacheRecordInput = {
    cacheKey,
    id: record.id,
    source: record.source,
    width: record.width,
    height: record.height,
    bytes: record.bytes,
    mimeType: record.mimeType,
    createdAt: record.createdAt,
    lastAccessedAt: record.lastAccessedAt,
    totalBytes: record.totalBytes,
    ...(record.attribution ? { attribution: record.attribution } : {}),
    edit: record.edit ?? {
      crop: { x: 0, y: 0, width: 100, height: 100 },
      targetWidth: record.width,
      targetHeight: record.height,
    },
    persisted,
    preview: record.preview,
    variants: record.variants.map(variant => ({
      scale: variant.scale,
      descriptor: variant.descriptor,
      blob: variant.blob,
      width: variant.width,
      height: variant.height,
      bytes: variant.bytes,
      mimeType: variant.mimeType,
    })),
  };

  await putRecord(input);
  return listManifests(cacheKey);
}
